import React, { useState, useEffect } from 'react';
import { DataGridPro, GridColDef, GridToolbar, GridRenderCellParams  } from '@mui/x-data-grid-pro';
import { useTheme, Box, Typography, Button, CircularProgress, Tabs, Tab, Alert, Stack,
          Table, TableBody, TableCell, TableContainer, TableRow, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';         
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useS3Manager } from '../../../../hooks/useS3Manager';
import { alpha, styled } from '@mui/material/styles';
import { getDatetimeDisplayString24h } from '../../../../utils/util';

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

interface ISnapshotlDataQualityReportProps {
  snapshot: ISnapshot | null;
  closeModal: () => void;
};

export default function SnapshotlDataQualityReport(props: ISnapshotlDataQualityReportProps) {

  const theme = useTheme();  
  const { isS3initialized, getObjectsForBucket, downloadJsonFile } = useS3Manager({ bucketName: process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET! }); 
  const [ dataQualityReports, setDataQualityReports ] = useState<Array<IDataQualityReport> | null>(null);  
  const [ logReports, setLogReports ] = useState<Array<ILogFile> | null>(null);  
  const [ isDownloading, setIsDownloading ] = useState<boolean>(false);  
  const [expanded, setExpanded] = React.useState<string | false>('panel0');
  const [tabValue, setTabValue] = React.useState(0);

  useEffect(() => { 

    if (!props.snapshot || !isS3initialized) return;

    const downloadReports = async () => {
  
      const dqReports : Array<IDataQualityReport> = [];
      const logReports : Array<ILogFile> = [];
      
      const rootPrefix = `${process.env.REACT_APP_AWS_S3_DATA_LAKE_ROOT_PATH}/silver/ama_${props.snapshot?.ClientId}/snapshot=${props.snapshot?.SnapshotDate}`;
      const dqrPrefix = `${rootPrefix}/data_quality/`;
      const logsPrefix = `${rootPrefix}/logs/`;

      setIsDownloading(true);

      try {

        // Async call to get reports
        const dqrItems = await getObjectsForBucket(process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET!, dqrPrefix);

        // Download DQ reports
        await Promise.all(dqrItems.map(async (key) => {
          const reportString = await downloadJsonFile(key);
          if (reportString) {
            // console.log(reportString);
            const dqReport : IDataQualityReport = JSON.parse(reportString);
            dqReports.push(dqReport);
          }
        }));

        // Set props      
        setDataQualityReports(dqReports);

      } catch (error) {
        console.error(error);
      } 

      try
      {        

        // Async call to get reports
        const logItems = await getObjectsForBucket(process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET!, logsPrefix);

        // Download log files
        await Promise.all(logItems.map(async (key) => {
          const logsetString  = await downloadJsonFile(key);
          if (logsetString) {  

            const fileName = key.split('/').pop()!;
            const pathParts = fileName.split('_');
            const timestamp = pathParts[pathParts.length - 1].split('.')[0];
            const displayTime = getDatetimeDisplayString24h(timestamp);
            const snapshot = pathParts[pathParts.length - 2];
            const displayName = `${displayTime} - ${snapshot}`;

            // Add to collection
            logReports.push({ 
              fileName: fileName,
              name: displayName,
              logEntries: JSON.parse(logsetString) });
          }
        }));

        // Set props      
        setLogReports(logReports.sort((a: ILogFile, b: ILogFile) => b.name.localeCompare(a.name)));

      } catch (error) {
        console.error(error);
      } 

      setIsDownloading(false);
    };

    downloadReports();

  }, [isS3initialized, props.snapshot]);

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };  

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;  
    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
        sx={{ width: '100%', position: 'relative', height: '100%', flex: '1', }}>

        {value === index && (
          <Box sx={{ height: '100%', width: '100%', p: 0 }}>
            {children}
          </Box>
        )}
      </Box>
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const cellStyle = {
    fontSize: '12px',
    overflowX: 'hidden',
    border: `solid 1px ${theme.palette.grey[100]}`
  };

  const CustomFooter = (count: number | undefined) => {
    return (
      <Stack sx={{ height: 30, backgroundColor: 'white' }} justifyContent="center" alignItems="center">
        Records: {count}
      </Stack>
    );
  };

  const RenderCopyColumn = (row: any) => {
    
    const clickHandler = async () => {
      navigator.clipboard.writeText(JSON.stringify(row));
    };

    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
        <IconButton 
          aria-label="Copy row to clipboard."
          sx={{ color: theme.palette.grey[500]}}
          onClick={clickHandler}>
          <ContentCopyIcon sx={{ fontSize: 'medium' }}/>
        </IconButton>
      </Box>
    );    
  }

  const dataQualityColumns: Array<GridColDef> = [  
    { 
      field: 'Rule', type: 'string',
      headerName: 'Rule', flex: .4, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'Outcome', type: 'string',
      headerName: 'Outcome', flex: .1, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'FailureReason', type: 'string',
      headerName: 'Fail Reason', flex: .25, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'EvaluatedMetrics', type: 'string',
      headerName: 'Metrics', flex: .25, 
      resizable: true, sortable: true,
      renderCell: (params) => (
        <pre>{JSON.stringify(params.value, null, 2).replace(/[{}]/g, '').trim()}</pre>
      ),
    }, 
    { 
      field: 'copy', type: 'string', headerName: '',
      resizable: false, sortable: false, flex: 0, width: 50,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderCopyColumn(params.row as IDataQualityReport) } </>)
    }
  ];

  const logColumns: Array<GridColDef> = [  
    { 
      field: 'logEntry', type: 'string', headerName: 'Log Entry', 
      flex: 1, 
      resizable: true, sortable: true      
    },
    { 
      field: 'copy', type: 'string', headerName: '',
      resizable: false, sortable: false, flex: 0, width: 50,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderCopyColumn(params.row as string) } </>)
    }
  ];

  return (
    <Box sx={{
      position: 'absolute' as const,
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      width: '80vw', height: '80vh',
      bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24,
      p: 4,
      display: 'flex', flexDirection: 'column',}}>

      {/* Loading overlay */}
      {isDownloading &&
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
        </div>
      }

      {/* Modal Header */}
      <Box sx={{ flex: 'none', position: 'relative' }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom sx={{pb: 0}}>
          Silver Job Artifacts
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ height: '100%', flex: '1', display: 'flex', flexDirection: 'column' }}>

        {/* Tabbed content */}
        <Box sx={{ height: 'auto', flex: 'none'}}>
          <Tabs value={tabValue} onChange={handleChange} aria-label="AMA tabbed features">
            <Tab label="Data Quality" {...a11yProps(0)} />
            <Tab label="Logging" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Data Quality Reports Content Section */}
        <CustomTabPanel value={tabValue} index={0}>            
          <Box sx={{ position: 'absolute', width: '100%', height: '100%', overflow: 'auto' }}>
            
            {dataQualityReports && dataQualityReports.map((report, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', mt: 0 }}>
                <Accordion 
                  key={`${index}`}
                  expanded={expanded === `panel${index}`} 
                  onChange={handleAccordionChange(`panel${index}`)}
                  sx={{ 
                    width: '100%', overflowX: 'hidden',                          
                    py: 0, my: 0                       
                  }}>

                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{ backgroundColor: 'white'  }}>
                    <Typography
                      variant='subtitle2'
                      color={theme.palette.text.secondary}
                      sx={{ mx: 0, fontSize: '0.9rem'}}>
                      {report.title}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ overflowY: 'auto', backgroundColor: theme.palette.grey[100],}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', }}>
                      <Box sx={{  flex: 'none', display: 'flex',  }}>
                        <TableContainer component={'div'} sx={{ backgroundColor: 'white' }}>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ ...cellStyle, width: '20%' }}><strong>Total Quality Checks</strong></TableCell>
                                <TableCell sx={{ ...cellStyle, width: '30%' }}>{report.total_dq_checks}</TableCell>
                                <TableCell sx={{ ...cellStyle, width: '20%' }}><strong>Data Quality Score</strong></TableCell>
                                <TableCell sx={{ ...cellStyle, width: '30%' }}>{report.dq_score}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ ...cellStyle }}><strong>Quality Checks Passed</strong></TableCell>
                                <TableCell sx={{ ...cellStyle }}>{report.total_dq_success}</TableCell>
                                <TableCell sx={{ ...cellStyle }}><strong>Data Quality Grade</strong></TableCell>
                                <TableCell sx={{ ...cellStyle }}>{report.dq_grade}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box> 
                      <Box sx={{ flex: '1', display: 'flex',  }}>
                        {report && report.dq_results &&                     
                          <DataGridPro
                            columns={dataQualityColumns}
                            rows={wrapDQResults(report.dq_results)}
                            rowThreshold={0}      
                            disableDensitySelector={true}
                            disableColumnResize={false}          
                            disableColumnSelector={true}
                            disableRowSelectionOnClick={true}
                            disableMultipleRowSelection={true}  
                            slots={{ 
                              toolbar: GridToolbar,
                              footer: () => CustomFooter(report.dq_results.length)
                            }}   
                            slotProps={{
                              toolbar: {
                                sx: { backgroundColor: 'white' },
                                showQuickFilter: true,            
                                printOptions: { disableToolbarButton: true },
                                csvOptions: {disableToolbarButton: true}           
                              }}}
                            getRowClassName={(params) =>
                              params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                            }           
                            sx={{
                              mt: 2,
                              '& .MuiDataGrid-root': {
                                color: theme => theme.palette.text.primary,
                                fontSize: '0.8rem'
                              },
                              '& .MuiDataGrid-columnHeader': {
                                backgroundColor: theme => theme.palette.background.paper,
                                color: theme => theme.palette.text.primary,
                                fontSize: '.8rem'
                              },
                              '& .MuiDataGrid-cell': {
                                backgroundColor: 'white',
                                color: theme => theme.palette.text.primary,
                                fontSize: '0.8rem'
                              }
                            }}
                          />
                        }
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}

            {dataQualityReports && dataQualityReports.length === 0 &&
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                <Alert severity="info" sx={{ width:'auto'}}>Silver Data Quality Report has not yet been generated!</Alert>
              </Box>
            }
            
          </Box>
        </CustomTabPanel>

        {/* Log Files Content Section */}
        <CustomTabPanel value={tabValue} index={1}>
          <Box sx={{ position: 'absolute', width: '100%', height: '100%', overflow: 'auto' }}>
            
            {logReports && logReports.map((logset, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', mt: 0 }}>
                <Accordion 
                  key={`${index}`}
                  expanded={expanded === `panel${index}`} 
                  onChange={handleAccordionChange(`panel${index}`)}
                  sx={{ 
                    width: '100%', overflowX: 'hidden',                          
                    py: 0, my: 0                       
                  }}>

                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{ backgroundColor: 'white'  }}>
                    <Typography
                      variant='h6'
                      color={theme.palette.text.secondary}
                      sx={{ mx: 2, fontSize: '0.9rem'}}>
                      {logset.name}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ overflowY: 'auto', backgroundColor: theme.palette.grey[100],}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', }}>
                      <Box sx={{ flex: '1', display: 'flex',  }}>
                        {logset &&                     
                          <DataGridPro
                            columns={logColumns}
                            rows={wrapLogset(logset.logEntries)}
                            rowThreshold={0}      
                            disableDensitySelector={true}
                            disableColumnResize={false}          
                            disableColumnSelector={true}
                            disableRowSelectionOnClick={true}
                            disableMultipleRowSelection={true}  
                            slots={{ 
                              toolbar: GridToolbar,
                              footer: () => CustomFooter(logset.logEntries.length)
                            }}   
                            slotProps={{
                              toolbar: {
                                sx: { backgroundColor: 'white' },
                                showQuickFilter: true,            
                                printOptions: { disableToolbarButton: true },
                                csvOptions: {disableToolbarButton: true}           
                              }
                            }}
                            getRowClassName={(params) =>
                              params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                            }           
                            sx={{
                              mt: 0,
                              '& .MuiDataGrid-root': {
                                color: theme => theme.palette.text.primary,
                                fontSize: '0.8rem'
                              },
                              '& .MuiDataGrid-columnHeader': {
                                backgroundColor: theme => theme.palette.background.paper,
                                color: theme => theme.palette.text.primary,
                                fontSize: '.8rem'
                              },
                              '& .MuiDataGrid-cell': {
                                backgroundColor: 'white',
                                color: theme => theme.palette.text.primary,
                                fontSize: '0.8rem'
                              }
                            }}
                          />
                        }
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
            
            {logReports && logReports.length === 0 &&
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                <Alert severity="info" sx={{ width:'auto'}}>Silver Logs have not yet been generated!</Alert>
              </Box>
            }

          </Box>
        </CustomTabPanel>
      </Box>

      {/* Footer buttons */}
      <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'center', pt: 2, pb: 0 }}>
        <Button variant="contained" color="primary" onClick={props.closeModal}>Close</Button>
      </Box> 
    </Box>
  );
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const wrapDQResults = (results: Array<IDataQualityResult>) => {
  let idCounter = 1;
  return results.map((jsonObject) => {
    const newObj = { ...jsonObject, id: idCounter };
    idCounter++;
    return newObj;
  });
};

const wrapLogset = (results: Array<string>) => {
  let idCounter = 1;
  return results.map((logEntry) => {
    const newObj = { logEntry, id: idCounter };
    idCounter++;
    return newObj;
  });
};
